<template>
    <modal-side-generic
    sizeClass="big"
    :title="t('payments.services.reuseModal.title')"
    :triggerClose="triggerClose"
    :tabs="reuseModalTabsList"
    @tabClicked="selectedTab = $event"
    @hideMe="[emit('hideMe'), triggerClose = false]">
        <!-- Loader -->
        <loader-generic
        v-if="showLoader || showOverlaySpinner"
        :fullSize="!showOverlaySpinner"
        :isOverlay="showOverlaySpinner"/>
        <!-- Error State -->
        <empty-state-error v-if="applyErrorState && !showLoader" :message="t('genericErrorMessage')"/>
        <!-- Tables -->
        <transition name="common-enter-animation" appear mode="out-in">
			<!-- Latest -->
            <div v-if="!showLoader && !applyErrorState && tableDataLatest.length > 0 && selectedTab === 'latest'" class="content-wrapper prevent-auto-margins">
				<table-generic
                :headerData="tableHeaderDataLatest"
                :tableData="tableDataLatest"
                :tableStyles="tableStyles"
				@sortBy="sortValue = $event"
                @rowClicked="tableRowClickHandler($event)"/>
			</div>
			<!-- Favorites -->
			<div
            v-else-if="!showLoader && !applyErrorState && tableDataFavorites.length > 0 && selectedTab === 'favorites'"
            class="content-wrapper prevent-auto-margins">
				<!-- Favorites -->
                <table-generic
                :headerData="tableHeaderDataFavorite"
                :tableData="tableDataFavorites"
                :tableStyles="tableStyles"
                :hasMore="getFavoritesHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('payments.services.reuseModal.loadingMoreText')"
                @loadMore="loadMoreHandler"
                @rowClicked="tableRowClickHandler($event)"/>
			</div>
		</transition>
        <!-- Empty State Latest -->
        <empty-state-generic
        v-if="!showLoader && !applyErrorState && !tableDataLatest.length && selectedTab === 'latest'"
        illustration="emptyState/no-movements"
        :title="t('payments.services.reuseModal.emptyState.lastPayments.title')"
        :description="t('payments.services.reuseModal.emptyState.lastPayments.description')"/>
        <!-- Empty State Favorites -->
        <empty-state-generic
        v-if="!showLoader && !applyErrorState && !tableDataFavorites.length && selectedTab === 'favorites'"
        illustration="emptyState/favorites-folder"
        :title="t('payments.services.reuseModal.emptyState.favorite.title')"
        :description="t('payments.services.reuseModal.emptyState.favorite.description')"/>
    </modal-side-generic>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Aux */
import { getDate } from '@/assets/scripts/dateBuilders'
import { currencyParser } from '@/assets/scripts/amounts'
import { paymentsFlowsRoute, parseOperatorIdForFavoriteTab } from '@/assets/scripts/mappers/payments'
import { navigateByName } from '@/router/helpers/navigation'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { sortTableData } from '@/assets/scripts/parsers/tables'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const {	paymentsListFetch, favoritesTabsFetch, favoritesListFetch, paymentDetailFetch } = mapActions(moduleMapper.PAYMENTS_SERVICES)
const {
    getPaymentDetail, getPaymentsList, getPaymentOperatorName, getFavoritesList,
    getFavoritesTabs, getFavoritesHasMore, getPaymentsOperatorsList
} = mapGetters(moduleMapper.PAYMENTS_SERVICES)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['hideMe'])

/* Show Data */
const showLoader = ref(false)
const showOverlaySpinner = ref(false)

/* Data */
const triggerClose = ref(false)
const applyErrorState = ref(false)

/**********************************************************************
*                          Reuse Modal Tabs                           *
**********************************************************************/
const selectedTab = ref('latest')

const reuseModalTabsList = computed(() => {
    const tabs = [
        { id: 'latest', label: t('payments.services.reuseModal.tabsList.latest') },
        { id: 'favorites', label: t('payments.services.reuseModal.tabsList.favorites') }
    ]

    return tabs.map(tab => {
        return { ...tab, selected: selectedTab.value === tab.id }
    })
})

/**********************************************************************
*                          Reuse Modal Table                          *
**********************************************************************/
const favoritesList = ref([])

const tableRowClickHandler = $event => {
    const rowClickData = selectedTab.value === 'latest'
    ? getPaymentsList.value.payments.find(tab => $event.rowId === tab.id)
    : favoritesList.value.find(tab => $event.rowId === tab.id)

    const rowClickFlow = selectedTab.value === 'latest'
    ? rowClickData.operator.flow
    : rowClickData.customerOperationView.operationType.id

    /* Parsed Payments Operators List Names */
    const parsedPaymentsOperatorsList = []
    getPaymentsOperatorsList.value.forEach(operatorGroups => {
        operatorGroups.children.forEach(operator => {
            operator.children.forEach(elem => {
                elem.parentName = operator.name
                elem.operatorGroupName = operatorGroups.name
                parsedPaymentsOperatorsList.push(elem)
            })
        })
    })

    /* Payment Details to send in the route params when we selected a favorite row */
    const getPaymentDetailFavorite = {}
    if (rowClickData.customerOperationView) {
        rowClickData.customerOperationView.parameters.forEach(el => {
            getPaymentDetailFavorite[el.name] = !isNaN(el.value) && (el.key !== 'amount') ? parseInt(el.value) : el.value
        })
    }

    if (selectedTab.value === 'latest') {
        const paymentInfo = parsedPaymentsOperatorsList.find(el => el.id === rowClickData.operator.id)
        paymentDetailLastestRequest(rowClickData, rowClickFlow, paymentInfo)
    } else {
        /* Payment Info to send in the route params when we selected a tab */
        let paymentInfo

        const paymentInfoByEntityId = parsedPaymentsOperatorsList.find(el => el.data?.emisReference && parseInt(el.data?.emisReference) === getPaymentDetailFavorite.entityId)
        const paymentInfoByOperatorId = parsedPaymentsOperatorsList.find(el => el.id === getPaymentDetailFavorite.operatorId)

        if (paymentInfoByEntityId || paymentInfoByOperatorId) {
            if (paymentInfoByEntityId) paymentInfo = paymentInfoByEntityId
            else paymentInfo = paymentInfoByOperatorId
            /* Add final action to navigate to another page */
            navigateByName(paymentsFlowsRoute(paymentInfo.flow), { params: { ...rowClickData, ...paymentInfo, ...getPaymentDetailFavorite } })
        }
    }
}

/**********************************************************************
*                            Table Latest                             *
**********************************************************************/
const tableHeaderDataLatest = [
    {
        label: t('payments.services.reuseModal.tableHeaderData.serviceLabel'),
        dataAttr: 'paymentTypeDescription',
        showInMobile: { groupWith: 'amount' },
        rowCellType: 'title',
        sortable: true
    },
    { label: t('payments.services.reuseModal.tableHeaderData.dateLabel'), dataAttr: 'lastChangeDate', sortable: true },
    { label: t('payments.services.reuseModal.tableHeaderData.amountLabel'), dataAttr: 'amount', sortable: true }
]

const sortValue = ref(null)
const tableDataLatest = computed(() => {
    if (!getPaymentsList.value) return []

    const latestPayments = getPaymentsList.value.payments.map(payment => {
        return [
            { value: payment.id, dataAttr: 'id', initialSortDirection: 'Desc' },
            { type: 'title', value: payment.paymentTypeDescription, dataAttr: 'paymentTypeDescription' },
            {
                value: {
                    content: getDate(payment.lastChangeDate),
                    classes: !getMobileAppState.value ? 'capitalize' : ['capitalize', 'small', 'secondary-color']
                },
                dataAttr: 'lastChangeDate'
            },
            { type: 'amount', value: { content: payment.amount, currency: currencyParser(payment.currency), noBalanceIcon: true }, dataAttr: 'amount' }
        ]
    })

    return sortValue.value ? sortTableData(latestPayments, sortValue.value) : latestPayments
})

const tableStyles = [{ width: '280px', 'min-width': '280px' }, { width: '105px', 'min-width': '105px' }, { 'text-align': 'end' }]

/**********************************************************************
*                           Table Favorite                            *
**********************************************************************/
const tableHeaderDataFavorite = [
    {
        label: t('payments.services.reuseModal.tableHeaderData.nameLabel'),
        dataAttr: 'name',
        showInMobile: { groupWith: 'amount' },
        rowCellType: 'title'
    },
    { label: t('payments.services.reuseModal.tableHeaderData.entityLabel'), dataAttr: 'entity' },
    { label: t('payments.services.reuseModal.tableHeaderData.amountLabel'), dataAttr: 'amount', rowCellType: 'amount' }
]

const tableDataFavorites = computed(() => {
    if (!favoritesList.value) return []

    return favoritesList.value.map(favorite => {
        const operatorId = favorite.customerOperationView.parameters.find(el => el.name === 'operatorId')

        return [
            { value: favorite.id, dataAttr: 'id' },
            { type: 'title', value: favorite.alias, dataAttr: 'name' },
            { type: 'description', value: operatorId ? getPaymentOperatorName.value(operatorId.value) : '-', dataAttr: 'entity' },
            {
                type: 'amount',
                value: {
                    content: favorite.customerOperationView.parameters.find(el => el.name === 'amount').value,
                    currency: favorite.customerOperationView.parameters.find(el => el.name === 'currency')?.value ? currencyParser(favorite.customerOperationView.parameters.find(el => el.name === 'currency').value) : ACCOUNT_MAIN_CURRENCY,
                    noBalanceIcon: true
                },
                dataAttr: 'amount'
            }
        ]
    })
})

/*************************************************
*                                                *
*               Load More Favorites              *
*                                                *
*************************************************/
const tableLoadingMore = ref(false)
const favoritesPayload = computed(() => {
    return {
        operationTypes: getFavoritesTabs.value.favoriteTabs.find(type => type.id === 2).operationTypeList.map(el => el.id),
        pageSize: 10,
        pageNumber: 1
    }
})

const loadMoreFavorites = async () => {
    tableLoadingMore.value = true

    try {
        await favoritesListFetch(favoritesPayload.value)
        favoritesList.value = getFavoritesList.value
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, loadMoreFavorites)
    }
}

const loadMoreHandler = () => {
    favoritesPayload.value.pageNumber++
    loadMoreFavorites()
}

/*************************************************
*                                                *
*             Payment Detail Requests            *
*                                                *
*************************************************/
const paymentDetailLastestRequest = async (rowClickData, rowClickFlow, paymentInfo) => {
    applyErrorState.value = false
    showOverlaySpinner.value = true

    const paymentDetailPayload = {
        paymentId: rowClickData.id
	}

	try {
        await paymentDetailFetch(paymentDetailPayload)

        if (getPaymentDetail.value) await navigateByName(paymentsFlowsRoute(rowClickFlow), { params: { ...rowClickData, ...getPaymentDetail.value, ...paymentInfo } })

        showOverlaySpinner.value = false
        triggerClose.value = true
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, paymentDetailLastestRequest)
	}
}

/*************************************************
*                                                *
*                Initial Requests                *
*                                                *
*************************************************/
const initialRequest = async () => {
    applyErrorState.value = false
	showLoader.value = true

    const paymentsListPayload = {
        pageSize: 10,
        pageNumber: 1,
        status: 1
	}

	try {
        await paymentsListFetch(paymentsListPayload)
        await favoritesTabsFetch()
        await favoritesListFetch(favoritesPayload.value)
        favoritesList.value = getFavoritesList.value
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequest)
	}
}

/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlaySpinner.value = false
    applyErrorState.value = true

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequest()
</script>

<style lang="scss" scoped>
.content-wrapper {
    @include commonEnteringAnimation;
    width: 100%;
}
</style>
